import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Snooze as SnoozeIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/ui/PageHeader';
import axios from '../../utils/axios';
import { notify } from '../../utils/notify';
import {
  ForecastMetadataChips,
  cohortLabel,
  extractForecastMetadata,
  fmtFactor,
  fmtNumber,
  fmtPercent,
  marketplaceLabel,
  metricLabel,
  modelLabel,
  reasonLabel,
  STATUS_LABELS,
} from './forecastMeta';

const TABS = [
  'Рекомендации',
  'Сравнение моделей',
  'Правила выбора',
  'История',
];

const STATUS_COLOR = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  snoozed: 'default',
  active: 'success',
  inactive: 'default',
};

const API_PREFIX = '/analytics/forecast-lab';

function asArray(data, keys = []) {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
}

function firstValue(row, keys, fallback = '—') {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return fallback;
}

function MetricLine({ label, row, type = 'recommended' }) {
  const isCurrent = type === 'current';
  const wape = firstValue(row, isCurrent ? ['current_wape', 'baseline_wape', 'previous_wape'] : ['recommended_wape', 'wape', 'new_wape'], null);
  const bias = firstValue(row, isCurrent ? ['current_bias', 'baseline_bias', 'previous_bias'] : ['recommended_bias', 'bias', 'new_bias'], null);
  const fact = firstValue(row, isCurrent ? ['current_fact_qty', 'baseline_fact_qty', 'previous_fact_qty'] : ['recommended_fact_qty', 'fact_qty', 'actual_qty'], null);

  if (wape == null && bias == null && fact == null) return null;

  return (
    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
      <Typography variant="caption" color="text.primary" fontWeight={700}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        WAPE: {fmtPercent(wape)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Смещение: {fmtPercent(bias)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Факт: {fmtNumber(fact, 0)} шт
      </Typography>
    </Stack>
  );
}

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function statusChip(status) {
  const value = status || 'pending';
  return (
    <Chip
      size="small"
      label={STATUS_LABELS[value] || 'Статус'}
      color={STATUS_COLOR[value] || 'default'}
      variant={value === 'pending' || value === 'active' ? 'filled' : 'outlined'}
    />
  );
}

function SegmentSummary({ row }) {
  const group = firstValue(row, ['gruppa', 'group', 'product_group'], 'Все группы');
  const cohort = firstValue(row, ['cohort', 'sku_cohort'], 'overall');
  const horizon = firstValue(row, ['horizon_bucket', 'horizonBucket'], 'весь горизонт');
  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
      <Chip size="small" variant="outlined" label={marketplaceLabel(firstValue(row, ['mp', 'marketplace'], ''))} />
      <Chip size="small" variant="outlined" label={String(group)} />
      <Chip size="small" variant="outlined" label={cohortLabel(cohort)} />
      <Chip size="small" variant="outlined" label={`Горизонт: ${horizon}`} />
    </Stack>
  );
}

function ReasonChips({ row }) {
  const raw = firstValue(row, ['reason_codes', 'reasons', 'reason_code', 'reason'], []);
  const reasons = Array.isArray(raw) ? raw : String(raw || '').split(',').map((x) => x.trim()).filter(Boolean);
  if (!reasons.length) {
    return <Typography variant="body2" color="text.secondary">Причина не указана</Typography>;
  }
  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
      {reasons.map((reason, idx) => (
        <Chip key={`${reason}_${idx}`} size="small" label={reasonLabel(reason)} variant="outlined" />
      ))}
    </Stack>
  );
}

export default function ForecastLab() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [apiWarning, setApiWarning] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [routing, setRouting] = useState([]);
  const [history, setHistory] = useState([]);

  const loadRecommendations = useCallback(async () => {
    const { data } = await axios.get(`${API_PREFIX}/recommendations`, { silent: true });
    setRecommendations(asArray(data, ['items', 'rows', 'recommendations']));
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setApiWarning('');
    const results = await Promise.allSettled([
      loadRecommendations(),
      axios.get(`${API_PREFIX}/comparison`, { silent: true }),
      axios.get(`${API_PREFIX}/routing`, { silent: true }),
      axios.get(`${API_PREFIX}/history`, { silent: true }),
    ]);

    const [, comparisonResult, routingResult, historyResult] = results;
    if (comparisonResult.status === 'fulfilled') {
      setComparison(asArray(comparisonResult.value.data, ['items', 'rows', 'comparison', 'models']));
    } else {
      setComparison([]);
    }
    if (routingResult.status === 'fulfilled') {
      setRouting(asArray(routingResult.value.data, ['items', 'rows', 'rules', 'routing']));
    } else {
      setRouting([]);
    }
    if (historyResult.status === 'fulfilled') {
      const historyData = historyResult.value.data;
      const historyRows = asArray(historyData, ['items', 'rows', 'history']);
      setHistory(historyRows.length ? historyRows : [
        ...asArray(historyData, ['recommendations']),
        ...asArray(historyData, ['routing']),
      ]);
    } else {
      setHistory([]);
    }

    if (results.some((r) => r.status === 'rejected')) {
      setApiWarning('Часть данных Forecast Lab пока недоступна. Интерфейс готов к контракту API и покажет данные после включения бэкенда.');
    }
    setLoading(false);
  }, [loadRecommendations]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const counters = useMemo(() => {
    const pending = recommendations.filter((r) => (r.status || 'pending') === 'pending').length;
    const activeRules = routing.filter((r) => (r.status || 'active') === 'active').length;
    return {
      pending,
      comparison: comparison.length,
      activeRules,
      history: history.length,
    };
  }, [recommendations, comparison, routing, history]);

  const runAction = async (row, action) => {
    const id = row?.id || row?.recommendation_id;
    if (!id) {
      notify('У рекомендации нет идентификатора для действия', { severity: 'warning' });
      return;
    }
    const actionLabel = action === 'approve' ? 'принята' : action === 'reject' ? 'отклонена' : 'отложена';
    setActionLoading(`${id}:${action}`);
    try {
      await axios.post(`${API_PREFIX}/recommendations/${id}/${action}`);
      notify(`Рекомендация ${actionLabel}`, { severity: 'success' });
      await loadRecommendations();
    } catch (_) {
      // Ошибка уже показана axios-интерцептором.
    } finally {
      setActionLoading('');
    }
  };

  const renderRecommendations = () => (
    <Grid container spacing={2}>
      {!recommendations.length && (
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, border: (t) => `1px solid ${t.palette.outlineVariant}`, borderRadius: 3 }}>
            <Typography color="text.secondary">Нет рекомендаций для выбора модели.</Typography>
          </Paper>
        </Grid>
      )}
      {recommendations.map((row, idx) => {
        const id = row.id || row.recommendation_id || idx;
        const recommendedModel = firstValue(row, ['recommended_model', 'model_name', 'model'], '');
        const currentModel = firstValue(row, ['current_model', 'baseline_model', 'previous_model'], '');
        const pending = (row.status || 'pending') === 'pending';
        return (
          <Grid item xs={12} md={6} xl={4} key={id}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                p: 2,
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.outlineVariant}`,
                bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.08 : 0.04),
              }}
            >
              <Stack spacing={1.5} sx={{ height: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle1" fontWeight={700}>
                    {modelLabel(recommendedModel)}
                  </Typography>
                  {statusChip(row.status)}
                </Stack>
                <SegmentSummary row={row} />
                {currentModel && (
                  <Typography variant="body2" color="text.secondary">
                    Сейчас: {modelLabel(currentModel)}
                  </Typography>
                )}
                <ReasonChips row={row} />
                <Stack spacing={0.5}>
                  <MetricLine label={`${modelLabel(recommendedModel)}:`} row={row} />
                  {currentModel && (
                    <MetricLine label={`${modelLabel(currentModel)}:`} row={row} type="current" />
                  )}
                </Stack>
                <Box sx={{ flexGrow: 1 }} />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    size="small"
                    color="success"
                    startIcon={actionLoading === `${id}:approve` ? <CircularProgress size={16} /> : <CheckIcon />}
                    disabled={!pending || !!actionLoading}
                    onClick={() => runAction(row, 'approve')}
                  >
                    Принять
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={actionLoading === `${id}:reject` ? <CircularProgress size={16} /> : <CloseIcon />}
                    disabled={!pending || !!actionLoading}
                    onClick={() => runAction(row, 'reject')}
                  >
                    Отклонить
                  </Button>
                  <Button
                    size="small"
                    startIcon={actionLoading === `${id}:snooze` ? <CircularProgress size={16} /> : <SnoozeIcon />}
                    disabled={!pending || !!actionLoading}
                    onClick={() => runAction(row, 'snooze')}
                  >
                    Отложить
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderComparison = () => (
    <Paper elevation={0} sx={{ border: (t) => `1px solid ${t.palette.outlineVariant}`, borderRadius: 3, overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Сегмент</TableCell>
              <TableCell>Модель</TableCell>
              <TableCell align="right">{metricLabel('WAPE')}</TableCell>
              <TableCell align="right">{metricLabel('Bias')}</TableCell>
              <TableCell align="right">{metricLabel('MAPE')}</TableCell>
              <TableCell align="right">Факт, шт</TableCell>
              <TableCell align="right">Прогноз, шт</TableCell>
              <TableCell align="right">Окон</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!comparison.length && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Нет данных сравнения моделей</TableCell></TableRow>
            )}
            {comparison.map((row, idx) => (
              <TableRow key={row.id || idx} hover>
                <TableCell><SegmentSummary row={row} /></TableCell>
                <TableCell>{modelLabel(firstValue(row, ['model_name', 'model'], ''))}</TableCell>
                <TableCell align="right">{fmtPercent(firstValue(row, ['wape', 'WAPE'], null))}</TableCell>
                <TableCell align="right">{fmtPercent(firstValue(row, ['bias', 'Bias'], null))}</TableCell>
                <TableCell align="right">{fmtPercent(firstValue(row, ['mape', 'MAPE'], null))}</TableCell>
                <TableCell align="right">{fmtNumber(firstValue(row, ['fact_qty', 'actual_qty'], null), 0)}</TableCell>
                <TableCell align="right">{fmtNumber(firstValue(row, ['forecast_qty', 'predicted_qty'], null), 0)}</TableCell>
                <TableCell align="right">{fmtNumber(firstValue(row, ['windows_count', 'window_count', 'windows'], null), 0)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderRouting = () => (
    <Paper elevation={0} sx={{ border: (t) => `1px solid ${t.palette.outlineVariant}`, borderRadius: 3, overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Сегмент</TableCell>
              <TableCell>Выбранная модель</TableCell>
              <TableCell>{metricLabel('Calibration')}</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Обновлено</TableCell>
              <TableCell>Кем</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!routing.length && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>Нет активных правил выбора модели</TableCell></TableRow>
            )}
            {routing.map((row, idx) => {
              const meta = extractForecastMetadata(row);
              return (
                <TableRow key={row.id || idx} hover>
                  <TableCell><SegmentSummary row={row} /></TableCell>
                  <TableCell>
                    {meta ? <ForecastMetadataChips metadata={meta} /> : modelLabel(firstValue(row, ['selected_model', 'model_name', 'model'], ''))}
                  </TableCell>
                  <TableCell>{fmtFactor(firstValue(row, ['calibration_factor', 'factor'], null))}</TableCell>
                  <TableCell>{statusChip(row.status || 'active')}</TableCell>
                  <TableCell>{fmtDate(firstValue(row, ['updated_at', 'created_at', 'effective_from'], ''))}</TableCell>
                  <TableCell>{firstValue(row, ['updated_by_name', 'updated_by', 'created_by'], '—')}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderHistory = () => (
    <Paper elevation={0} sx={{ border: (t) => `1px solid ${t.palette.outlineVariant}`, borderRadius: 3, overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Дата</TableCell>
              <TableCell>Действие</TableCell>
              <TableCell>Сегмент</TableCell>
              <TableCell>Изменение</TableCell>
              <TableCell>Пользователь</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!history.length && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>История изменений пока пустая</TableCell></TableRow>
            )}
            {history.map((row, idx) => (
              <TableRow key={row.id || idx} hover>
                <TableCell>{fmtDate(firstValue(row, ['created_at', 'decided_at', 'updated_at'], ''))}</TableCell>
                <TableCell>{STATUS_LABELS[firstValue(row, ['action', 'status'], '')] || 'Изменение'}</TableCell>
                <TableCell><SegmentSummary row={row} /></TableCell>
                <TableCell>
                  <Tooltip title="Предыдущая и новая модель прогноза">
                    <Typography variant="body2">
                      {modelLabel(firstValue(row, ['previous_model', 'old_model'], ''))} → {modelLabel(firstValue(row, ['new_model', 'selected_model', 'model_name'], ''))}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>{firstValue(row, ['user_name', 'updated_by_name', 'updated_by', 'created_by'], '—')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <>
      <PageHeader
        title="Forecast Lab"
        subtitle="Ручное подтверждение рекомендаций выбора модели, сравнение качества и правила маршрутизации прогноза"
        breadcrumbs={[{ label: 'Аналитика', path: '/analytics' }, { label: 'Forecast Lab' }]}
        actions={(
          <Button startIcon={<RefreshIcon />} onClick={loadAll} disabled={loading}>
            Обновить
          </Button>
        )}
      />

      {apiWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {apiWarning}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Ожидают решения" value={counters.pending} /></Grid>
        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Строк сравнения" value={counters.comparison} /></Grid>
        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Активных правил" value={counters.activeRules} /></Grid>
        <Grid item xs={12} sm={6} md={3}><SummaryCard title="Записей истории" value={counters.history} /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ mb: 2, border: (t) => `1px solid ${t.palette.outlineVariant}`, borderRadius: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {TABS.map((label) => <Tab key={label} label={label} />)}
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tab === 0 && renderRecommendations()}
          {tab === 1 && renderComparison()}
          {tab === 2 && renderRouting()}
          {tab === 3 && renderHistory()}
        </>
      )}
    </>
  );
}

function SummaryCard({ title, value }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: (t) => `1px solid ${t.palette.outlineVariant}`,
        bgcolor: (t) => t.palette.surfaceContainerLow,
      }}
    >
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="h5" fontWeight={700}>{fmtNumber(value, 0)}</Typography>
    </Paper>
  );
}
